import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  deleteDoc, 
  updateDoc,
  query,
  where 
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  deleteUser,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { auth, db } from '../firebaseConfig.js';
import {
  FaArrowLeft,
  FaPlus,
  FaUserShield,
  FaUserGraduate,
  FaTrash,
  FaEdit,
  FaSave,
  FaTimes,
  FaSearch,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaExclamationTriangle
} from 'react-icons/fa';
import './GerenciarMateriais.css'; // Reutilizando estilos
import { normalizeText } from '../utils/text';

function GerenciarAdministradores() {
  const navigate = useNavigate();
  
  // Estados do formulário
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [role, setRole] = useState('especialista'); // admin ou especialista
  const [telefone, setTelefone] = useState('');
  const [bio, setBio] = useState('');
  
  // Estados da lista
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState('');
  
  // Estados da edição
  const [editandoId, setEditandoId] = useState(null);
  const [dadosEdicao, setDadosEdicao] = useState({});
  
  // Estados da UI
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [usuarioParaDeletar, setUsuarioParaDeletar] = useState(null);

  useEffect(() => {
    carregarUsuarios();
  }, []);

  useEffect(() => {
    const normalizedBusca = normalizeText(busca);
    const filtrados = usuarios.filter(user => 
      normalizeText(user.name).includes(normalizedBusca) ||
      normalizeText(user.email).includes(normalizedBusca) ||
      normalizeText(user.role).includes(normalizedBusca)
    );
    setUsuariosFiltrados(filtrados);
  }, [usuarios, busca]);

  const carregarUsuarios = async () => {
    try {
      // Primeiro, buscar admins da coleção antiga 'admins'
      const adminsRef = collection(db, 'admins');
      const adminsSnapshot = await getDocs(adminsRef);
      const adminsAntigos = adminsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        role: 'admin', // Forçar role admin para dados da coleção antiga
        source: 'admins' // Identificar origem
      }));

      // Depois, buscar admins e especialistas da nova coleção 'usuarios'
      const usuariosRef = collection(db, 'usuarios');
      const usuariosSnapshot = await getDocs(usuariosRef);
      const usuariosNovos = usuariosSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data(), source: 'usuarios' }))
        .filter(user => user.role === 'admin' || user.role === 'especialista');

      // Combinar ambas as listas
      const todosUsuarios = [...adminsAntigos, ...usuariosNovos];
      
      // Ordenar por role (admin primeiro) e depois por nome
      todosUsuarios.sort((a, b) => {
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        return (a.name || a.nome || '').localeCompare(b.name || b.nome || '');
      });
      
      setUsuarios(todosUsuarios);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setMensagem('Erro ao carregar usuários: ' + error.message);
    }
  };

  const limparFormulario = () => {
    setNome('');
    setEmail('');
    setSenha('');
    setRole('especialista');
    setTelefone('');
    setBio('');
    setMensagem('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMensagem('');

    try {
      // Validações
      if (senha.length < 6) {
        throw new Error('A senha deve ter pelo menos 6 caracteres');
      }

      // Criar usuário no Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, senha);
      const firebaseUser = userCredential.user;

      // Definir plano baseado no role
      const planoUsuario = role === 'especialista' ? 'Especialista' : 'Premium';

      // Salvar dados na nova coleção 'usuarios' (padrão)
      await addDoc(collection(db, 'usuarios'), {
        uid: firebaseUser.uid,
        name: nome,
        email: email,
        role: role,
        telefone: telefone || '',
        bio: bio || '',
        plano: planoUsuario, // Especialista ou Premium para Admin
        createdAt: new Date(),
        active: true
      });

      setMensagem(`${role === 'admin' ? 'Administrador' : 'Especialista'} criado com sucesso!`);
      limparFormulario();
      carregarUsuarios();

    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      let mensagemErro = 'Erro ao criar usuário: ';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          mensagemErro += 'Este e-mail já está em uso.';
          break;
        case 'auth/invalid-email':
          mensagemErro += 'E-mail inválido.';
          break;
        case 'auth/weak-password':
          mensagemErro += 'Senha muito fraca.';
          break;
        default:
          mensagemErro += error.message;
      }
      
      setMensagem(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const iniciarEdicao = (usuario) => {
    setEditandoId(usuario.id);
    setDadosEdicao({
      name: usuario.name || usuario.nome || '', // Compatibilidade com coleção antiga
      role: usuario.role || 'admin', // Default admin para coleção antiga
      telefone: usuario.telefone || '',
      bio: usuario.bio || '',
      active: usuario.active !== false
    });
  };

  const cancelarEdicao = () => {
    setEditandoId(null);
    setDadosEdicao({});
  };

  const salvarEdicao = async (usuarioId) => {
    setLoading(true);
    try {
      const usuario = usuarios.find(u => u.id === usuarioId);
      const collection_name = usuario?.source === 'admins' ? 'admins' : 'usuarios';
      
      if (collection_name === 'admins') {
        // Para coleção antiga, usar campos originais
        await updateDoc(doc(db, collection_name, usuarioId), {
          nome: dadosEdicao.name, // Note: 'nome' na coleção antiga
          active: dadosEdicao.active,
          // role não existe na coleção antiga
        });
      } else {
        // Para nova coleção, usar campos novos e atualizar plano baseado no role
        const novoPlano = dadosEdicao.role === 'especialista' ? 'Especialista' : 'Premium';
        
        await updateDoc(doc(db, collection_name, usuarioId), {
          name: dadosEdicao.name,
          role: dadosEdicao.role,
          plano: novoPlano, // Atualizar plano junto com role
          telefone: dadosEdicao.telefone,
          bio: dadosEdicao.bio,
          active: dadosEdicao.active,
          updatedAt: new Date()
        });
      }
      
      setMensagem('Usuário atualizado com sucesso!');
      cancelarEdicao();
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      setMensagem('Erro ao atualizar usuário: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmarDelecao = (usuario) => {
    setUsuarioParaDeletar(usuario);
    setShowConfirmModal(true);
  };

  const deletarUsuario = async () => {
    if (!usuarioParaDeletar) return;
    
    setLoading(true);
    try {
      // Identificar a coleção correta baseada na origem
      const collection_name = usuarioParaDeletar.source === 'admins' ? 'admins' : 'usuarios';
      
      // Deletar da coleção apropriada
      await deleteDoc(doc(db, collection_name, usuarioParaDeletar.id));
      
      setMensagem('Usuário deletado com sucesso!');
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      setMensagem('Erro ao deletar usuário: ' + error.message);
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
      setUsuarioParaDeletar(null);
    }
  };

  const getRoleColor = (userRole) => {
    switch (userRole) {
      case 'admin':
        return '#e74c3c';
      case 'especialista':
        return '#3498db';
      default:
        return '#6c757d';
    }
  };

  const getRoleIcon = (userRole) => {
    switch (userRole) {
      case 'admin':
        return <FaUserShield />;
      case 'especialista':
        return <FaUserGraduate />;
      default:
        return <FaUserShield />;
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="gerenciar-materiais admin-panel-container">
      {/* Modal de confirmação */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 style={{ color: '#e74c3c', marginBottom: '1rem' }}>
              <FaExclamationTriangle style={{ marginRight: '0.5rem' }} />
              Confirmar Exclusão
            </h2>
            <p style={{ marginBottom: '2rem', lineHeight: '1.6' }}>
              Tem certeza que deseja deletar o usuário <strong>{usuarioParaDeletar?.name}</strong>?
              <br />
              <small style={{ color: '#6c757d' }}>
                Esta ação não pode ser desfeita. O usuário perderá acesso ao sistema.
              </small>
            </p>
            <div className="modal-actions">
              <button 
                onClick={deletarUsuario} 
                className="deletar"
                disabled={loading}
              >
                <FaTrash /> {loading ? 'Deletando...' : 'Sim, Deletar'}
              </button>
              <button 
                onClick={() => setShowConfirmModal(false)} 
                className="fechar-button"
              >
                <FaTimes /> Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="admin-header">
        <button className="back-button" onClick={() => navigate('/admin-panel')}>
          <FaArrowLeft /> Voltar
        </button>
        <div className="header-title">
          <h1>Gerenciar Administradores</h1>
          <p>Cadastro e gestão de administradores e especialistas do sistema</p>
        </div>
      </header>

      <div className="admin-content">
        {/* Formulário de Cadastro */}
        <div className="admin-form-card">
          <h2>
            <FaPlus style={{ marginRight: '0.5rem' }} />
            Cadastrar Novo Usuário
          </h2>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Nome Completo *</label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                  placeholder="Digite o nome completo"
                />
              </div>

              <div className="form-group">
                <label>E-mail *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="usuario@exemplo.com"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Senha *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={mostrarSenha ? 'text' : 'password'}
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    required
                    placeholder="Mínimo 6 caracteres"
                    style={{ paddingRight: '3rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenha(!mostrarSenha)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6c757d'
                    }}
                  >
                    {mostrarSenha ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Cargo *</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  required
                >
                  <option value="especialista">Especialista</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Telefone</label>
                <input
                  type="tel"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div className="form-group">
                <label>Bio</label>
                <input
                  type="text"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Breve descrição (opcional)"
                />
              </div>
            </div>

            <div style={{
              padding: '1rem',
              background: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: '#6c757d'
            }}>
              <strong>📋 Plano automático:</strong> {role === 'especialista' ? 'Especialista' : 'Premium'} 
              <br />
              <small>O plano será definido automaticamente baseado no cargo selecionado.</small>
            </div>

            <button type="submit" disabled={loading}>
              <FaPlus />
              {loading ? 'Criando...' : `Criar ${role === 'admin' ? 'Administrador' : 'Especialista'}`}
            </button>
          </form>

          {mensagem && (
            <div className={`mensagem ${mensagem.includes('Erro') ? 'erro' : 'sucesso'}`}>
              {mensagem}
            </div>
          )}
        </div>

        {/* Lista de Usuários */}
        <div className="admin-list">
          <h2>
            Lista de Usuários ({usuariosFiltrados.length})
          </h2>

          <div className="search-bar">
            <FaSearch />
            <input
              placeholder="Buscar por nome, e-mail ou cargo..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>

          <div className="admin-table">
            <div className="table-header" style={{ gridTemplateColumns: '1fr 200px 120px 120px 180px' }}>
              <span>Usuário</span>
              <span>E-mail</span>
              <span>Cargo</span>
              <span>Status</span>
              <span>Ações</span>
            </div>

            {usuariosFiltrados.map((usuario) => (
              <div key={usuario.id} className="table-row" style={{ gridTemplateColumns: '1fr 200px 120px 120px 180px' }}>
                {editandoId === usuario.id ? (
                  // Modo edição
                  <>
                    <span>
                      <input
                        type="text"
                        value={dadosEdicao.name || ''}
                        onChange={(e) => setDadosEdicao(prev => ({ ...prev, name: e.target.value }))}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #dee2e6', borderRadius: '4px' }}
                      />
                    </span>
                    <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                      {usuario.email}
                    </span>
                    <span>
                      <select
                        value={dadosEdicao.role || 'especialista'}
                        onChange={(e) => setDadosEdicao(prev => ({ ...prev, role: e.target.value }))}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #dee2e6', borderRadius: '4px' }}
                      >
                        <option value="especialista">Especialista</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </span>
                    <span>
                      <select
                        value={dadosEdicao.active ? 'ativo' : 'inativo'}
                        onChange={(e) => setDadosEdicao(prev => ({ ...prev, active: e.target.value === 'ativo' }))}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #dee2e6', borderRadius: '4px' }}
                      >
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                      </select>
                    </span>
                    <span className="actions">
                      <button
                        onClick={() => salvarEdicao(usuario.id)}
                        className="ver-conteudo"
                        disabled={loading}
                        title="Salvar alterações"
                      >
                        <FaSave /> Salvar
                      </button>
                      <button
                        onClick={cancelarEdicao}
                        className="deletar"
                        title="Cancelar edição"
                      >
                        <FaTimes /> Cancelar
                      </button>
                    </span>
                  </>
                ) : (
                  // Modo visualização
                  <>
                    <span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: getRoleColor(usuario.role),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '1rem'
                          }}
                        >
                          {getRoleIcon(usuario.role)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                            {usuario.name || usuario.nome || 'Sem nome'}
                          </div>
                          {usuario.telefone && (
                            <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                              {usuario.telefone}
                            </div>
                          )}
                          {usuario.plano && (
                            <div style={{ fontSize: '0.7rem', color: '#28a745', fontWeight: '600' }}>
                              Plano: {usuario.plano}
                            </div>
                          )}
                        </div>
                      </div>
                    </span>
                    
                    <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                      {usuario.email}
                    </span>
                    
                    <span>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          background: `${getRoleColor(usuario.role)}20`,
                          color: getRoleColor(usuario.role)
                        }}
                      >
                        {getRoleIcon(usuario.role)}
                        {usuario.role === 'admin' ? 'Admin' : 'Especialista'}
                      </div>
                    </span>
                    
                    <span>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          background: usuario.active !== false ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          color: usuario.active !== false ? '#16a34a' : '#dc2626'
                        }}
                      >
                        <FaCheck />
                        {usuario.active !== false ? 'Ativo' : 'Inativo'}
                      </div>
                    </span>
                    
                    <span className="actions">
                      <button
                        onClick={() => iniciarEdicao(usuario)}
                        className="ver-conteudo"
                        title="Editar usuário"
                      >
                        <FaEdit /> Editar
                      </button>
                      <button
                        onClick={() => confirmarDelecao(usuario)}
                        className="deletar"
                        title="Deletar usuário"
                      >
                        <FaTrash /> Deletar
                      </button>
                    </span>
                  </>
                )}
              </div>
            ))}

            {usuariosFiltrados.length === 0 && (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '3rem',
                color: '#6c757d'
              }}>
                {busca ? (
                  <>
                    <FaSearch style={{ fontSize: '2rem', marginBottom: '1rem', opacity: 0.5 }} />
                    <p>Nenhum usuário encontrado para "{busca}"</p>
                  </>
                ) : (
                  <>
                    <FaUserShield style={{ fontSize: '2rem', marginBottom: '1rem', opacity: 0.5 }} />
                    <p>Nenhum administrador ou especialista cadastrado ainda.</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Informações adicionais */}
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '12px'
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
            ℹ️ Informações sobre os Cargos
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div>
              <h4 style={{ 
                color: '#e74c3c', 
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <FaUserShield /> Administrador (Plano: Premium)
              </h4>
              <ul style={{ paddingLeft: '1.5rem', color: '#6c757d', lineHeight: '1.6' }}>
                <li>Acesso completo ao sistema</li>
                <li>Pode gerenciar usuários e especialistas</li>
                <li>Materiais marcados como "Verificados"</li>
                <li>Pode aprovar/rejeitar materiais</li>
                <li>Gerencia planos e configurações</li>
              </ul>
            </div>
            
            <div>
              <h4 style={{ 
                color: '#3498db', 
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <FaUserGraduate /> Especialista (Plano: Especialista)
              </h4>
              <ul style={{ paddingLeft: '1.5rem', color: '#6c757d', lineHeight: '1.6' }}>
                <li>Pode enviar e gerenciar materiais</li>
                <li>Responde perguntas dos alunos</li>
                <li>Aprova materiais enviados por alunos</li>
                <li>Materiais marcados como "Verificados"</li>
                <li>Acesso total a todos os conteúdos</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GerenciarAdministradores;
